import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircleArrowDown, Clock3, Crosshair, ListOrdered } from "lucide-react";
import GuestRiderBadge from "@/components/GuestRiderBadge";
import TeamLogo from "@/components/TeamLogo";
import { asArray } from "@/lib/asArray";
import { isGuestRiderForTeam } from "@/lib/guestRiders";
import { cn } from "@/lib/utils";
import { getNativePlatform } from "@/lib/runtime";
import { computeHeatBonuses } from "@/lib/computeHeatBonuses";
import { formatHeatTimeDisplay } from "@/lib/heatTime";
import { getHelmetAccentStyle } from "@/lib/helmetAccent";

const HEAT_NUMBERS = Array.from({ length: 15 }, (_, index) => index + 1);
const HEAT_COLUMNS_WITH_NOMINATION = [...HEAT_NUMBERS.slice(0, 14), "nomination", 15];

function getHeatTargetKey(heatNumber) {
  return `heat:${heatNumber}`;
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const compact = parts[0].slice(0, 2).toUpperCase().split("");
    return compact.join(".");
  }
  return `${(parts[0][0] || "").toUpperCase()}.${(parts[parts.length - 1][0] || "").toUpperCase()}`;
}

function getRiderName(entry) {
  return entry?.name || entry?.rider_name || entry?.rider || entry?.rider_id || "Okänd";
}

function getRiderKey(entry, fallbackSide = "") {
  const riderId = entry?.rider_id ?? entry?.id;
  if (riderId) return `id:${riderId}`;
  const lineupNo = entry?.lineup_no ?? entry?.number;
  if (lineupNo) return `${entry?.team || fallbackSide}:lineup:${lineupNo}`;
  return `${entry?.team || fallbackSide}:name:${getRiderName(entry)}`;
}

function getLineupNo(entry) {
  const value = entry?.lineup_no ?? entry?.number;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getSortedLineup(entries = [], side = "") {
  return asArray(entries)
    .map((entry) => ({ ...entry, team: entry?.team || side }))
    .filter((entry) => getRiderName(entry))
    .sort((a, b) => {
      const aNo = getLineupNo(a) ?? Number.POSITIVE_INFINITY;
      const bNo = getLineupNo(b) ?? Number.POSITIVE_INFINITY;
      if (aNo !== bNo) return aNo - bNo;
      return getRiderName(a).localeCompare(getRiderName(b), "sv");
    });
}

function getHelmetMeta(color) {
  const normalized = String(color || "").trim().toLowerCase();
  if (["#dc2626", "#ef4444", "red", "r"].includes(normalized)) {
    return { label: "R", className: "bg-red-600 text-white border-red-600" };
  }
  if (["#1d4ed8", "#2563eb", "#3b82f6", "blue", "b"].includes(normalized)) {
    return { label: "B", className: "bg-blue-600 text-white border-blue-600" };
  }
  if (["#eab308", "#facc15", "yellow", "g"].includes(normalized)) {
    return { label: "G", className: "bg-yellow-300 text-slate-950 border-yellow-300" };
  }
  if (["#ffffff", "#fff", "white", "v"].includes(normalized)) {
    return { label: "V", className: "bg-white text-slate-950 border-slate-300" };
  }
  return { label: "-", className: "bg-muted text-muted-foreground border-border" };
}

function getMutedHelmetClass(label) {
  switch (String(label || "").toUpperCase()) {
    case "R":
      return "border-red-200 bg-red-100 text-red-800 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-100";
    case "B":
      return "border-blue-200 bg-blue-100 text-blue-800 dark:border-blue-500/30 dark:bg-blue-500/15 dark:text-blue-100";
    case "G":
      return "border-yellow-200 bg-yellow-100 text-yellow-900 dark:border-yellow-500/30 dark:bg-yellow-500/15 dark:text-yellow-100";
    case "V":
      return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-500/30 dark:bg-slate-500/15 dark:text-slate-100";
    default:
      return "border-border bg-muted text-foreground";
  }
}

function getHeatEntries(heat) {
  const riders = heat?.riders || heat?.lineup || heat?.entries || {};
  if (Array.isArray(riders)) {
    return riders.map((entry, index) => ({ gate: String(entry?.gate ?? index + 1), ...entry }));
  }
  return Object.entries(riders).map(([gate, entry]) => ({ gate: String(gate), ...entry }));
}

function buildHeatProgramRows(
  heats = [],
  lineups = {},
  showResults = false,
  showLineupChangeStatus = false,
  sourceLineups = {},
  lineupSubstitutions = {}
) {
  const sortedHeats = asArray(heats)
    .slice()
    .sort((a, b) => Number(a?.heat_number || 0) - Number(b?.heat_number || 0));

  const heatsByNumber = new Map(
    sortedHeats.map((heat) => [Number(heat?.heat_number || 0), heat])
  );

  const buildTeamSummary = (side) => {
    const heatPoints = {};
    let total = 0;

    HEAT_NUMBERS.forEach((heatNumber) => {
      const heat = heatsByNumber.get(heatNumber);
      const entries = getHeatEntries(heat);
      let score = 0;
      let hasResult = false;

      asArray(heat?.results).forEach((result) => {
        const entry = entries.find(
          (candidate) => String(candidate?.rider_id) === String(result?.rider_id)
        );
        if (entry?.team !== side) return;
        score += Number(result?.points || 0);
        hasResult = true;
      });

      if (hasResult) {
        heatPoints[heatNumber] = score;
        total += score;
      }
    });

    return { heatPoints, total };
  };

  const buildRows = (side) => {
    const lineupRows = getSortedLineup(lineups?.[side], side);
    const currentLineupKeys = new Set(lineupRows.map((entry) => getRiderKey(entry, side)));
    const sourceLineupRows = getSortedLineup(sourceLineups?.[side], side);
    const sourceByLineupNo = new Map(
      sourceLineupRows
        .map((entry) => [getLineupNo(entry), entry])
        .filter(([lineupNo]) => Boolean(lineupNo))
    );
    const substitutions = asArray(lineupSubstitutions?.[side]);
    const substituteIds = new Set(
      substitutions
        .map((entry) => String(entry?.current_substitute_rider_id || ""))
        .filter(Boolean)
    );
    const replacedIds = new Set(
      substitutions
        .map((entry) => String(entry?.original_rider_id || ""))
        .filter(Boolean)
    );
    const historicalRiderIdsByLineupNo = new Map();
    const fallbackRows = [];
    const seen = new Set(currentLineupKeys);

    sortedHeats.forEach((heat) => {
      getHeatEntries(heat)
        .filter((entry) => entry?.team === side)
        .forEach((entry) => {
          const lineupNo = getLineupNo(entry);
          const riderId = String(entry?.rider_id || entry?.id || "");
          if (lineupNo && riderId) {
            const existingIds = historicalRiderIdsByLineupNo.get(lineupNo) || new Set();
            existingIds.add(riderId);
            historicalRiderIdsByLineupNo.set(lineupNo, existingIds);
          }
          const key = getRiderKey(entry, side);
          if (seen.has(key)) return;
          seen.add(key);
          fallbackRows.push(entry);
        });
    });

      return [...lineupRows, ...getSortedLineup(fallbackRows, side)].map((entry) => {
        const key = getRiderKey(entry, side);
        const lineupNo = getLineupNo(entry);
        const isCurrentLineupRider = currentLineupKeys.has(key);
        const riderId = String(entry?.rider_id || entry?.id || "");
        const hasLineupChangeBase = sourceLineupRows.length > 0;
        const isSubstitute =
          hasLineupChangeBase &&
          isCurrentLineupRider &&
          Boolean(riderId) &&
          substituteIds.has(riderId);
        const isReplaced =
          hasLineupChangeBase &&
          !isCurrentLineupRider &&
          Boolean(riderId) &&
          replacedIds.has(riderId);
        const cells = {};
      let totalBase = 0;
      let totalBonus = 0;
      let starts = 0;

      HEAT_NUMBERS.forEach((heatNumber) => {
        const heat = heatsByNumber.get(heatNumber);
        const heatEntry = getHeatEntries(heat).find(
          (candidate) => getRiderKey(candidate, side) === key
        );

        if (!heatEntry) return;

        const result = asArray(heat?.results).find(
          (candidate) => String(candidate?.rider_id) === String(heatEntry?.rider_id)
        );
        const bonuses = showResults ? computeHeatBonuses(heat) || {} : {};
        const bonus = Number(bonuses?.[String(heatEntry?.rider_id)] || 0);
        const basePoints = Number(result?.points || 0);

        if (showResults && result?.status === "completed") {
          totalBase += basePoints;
          totalBonus += bonus;
        }
        if (showResults && heatEntry?.rider_id) {
          starts += 1;
        }

        cells[heatNumber] = {
          heat,
          gate: heatEntry.gate,
          helmetColor: heatEntry.helmet_color,
          helmet: getHelmetMeta(heatEntry.helmet_color),
          result,
          basePoints,
          bonus,
        };
      });

        return {
          key,
          name: getRiderName(entry),
          isGuest: isGuestRiderForTeam(entry),
          lineupNo: isCurrentLineupRider ? lineupNo : null,
          isReserve: Boolean(entry?.is_reserve || lineupNo === 6 || lineupNo === 7),
          isReplaced: showLineupChangeStatus ? isReplaced : false,
          isSubstitute: showLineupChangeStatus ? isSubstitute : false,
          cells,
          totalBase,
          totalBonus,
          starts,
      };
    });
  };

  return {
    hasHeats: sortedHeats.length > 0,
    home: buildRows("home"),
    away: buildRows("away"),
    teams: {
      home: buildTeamSummary("home"),
      away: buildTeamSummary("away"),
    },
    heatTimes: Object.fromEntries(
      sortedHeats
        .map((heat) => [Number(heat?.heat_number || 0), formatHeatTimeDisplay(heat?.heat_time || "")])
        .filter(([heatNumber, timeValue]) => heatNumber > 0 && Boolean(timeValue))
    ),
  };
}

function formatPoints(base, bonus) {
  if (!Number.isFinite(Number(base))) return "-";
  return Number(bonus) > 0 ? `${base}+${bonus}` : `${base}`;
}

function getStatusDisplay(result) {
  const status = String(result?.status || "").trim().toUpperCase();
  if (!status) return "-";
  if (status === "COMPLETED") return "-";
  if (status === "EXCLUDED") return "X";
  return status;
}

function getResultDisplay(cell, showResults) {
  if (!cell) return null;
  if (!showResults) return `${cell.helmet?.label || ""}${cell.gate || ""}`;
  if (cell?.result?.status === "completed") {
    return formatPoints(cell.basePoints, cell.bonus);
  }
  if (cell?.result?.status) {
    return getStatusDisplay(cell.result);
  }
  return `${cell.helmet?.label || ""}${cell.gate || ""}`;
}

export default function HeatProgramMatrix({
  heats,
  lineups,
  showLineupChangeStatus = false,
  sourceLineups,
  lineupSubstitutions,
  homeTeamName,
  awayTeamName,
  showResults = false,
  onHeatClick = null,
  onNominationClick = null,
  focusedHeatNumber = null,
  focusedTargetType = "heat",
  scrollToHeatNumber = null,
  scrollTargetType = "heat",
  focusRequestKey = null,
  onFocusedHeatVisibilityChange = null,
  showNominationColumn = false,
  nominationCompleted = false,
  nominationAvailable = false,
  emptyText = "Inget heatprogram tillgängligt för denna match.",
}) {
  const isNativeAndroid = getNativePlatform() === "android";
  const [isCompressed, setIsCompressed] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [isSnappedToRight, setIsSnappedToRight] = useState(false);
  const [pulseTargetKey, setPulseTargetKey] = useState(null);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const sidebarRef = useRef(null);
  const targetRefs = useRef(new Map());
  const nominationAnchorRef = useRef(null);
  const ignoreSidebarCollapseUntilRef = useRef(0);
  const scrollSettleTimeoutRef = useRef(null);
  const [nominationLayout, setNominationLayout] = useState(null);

  const matrix = useMemo(
    () =>
      buildHeatProgramRows(
        heats,
        lineups,
        showResults,
        showLineupChangeStatus,
        sourceLineups,
        lineupSubstitutions
      ),
    [heats, lineups, showResults, showLineupChangeStatus, sourceLineups, lineupSubstitutions]
  );
  const hasHeatTimes = useMemo(
    () => Object.values(matrix.heatTimes || {}).some(Boolean),
    [matrix.heatTimes]
  );

  const isSidebarManualExpanded = isCompressed && isSidebarExpanded;
  const isSidebarOverlayActive = isSidebarManualExpanded && isSnappedToRight;
  const isSidebarCompact = isCompressed && (!isSidebarExpanded || isSidebarOverlayActive);
  const activeHeatNumber = Number.isFinite(Number(focusedHeatNumber)) && Number(focusedHeatNumber) > 0
    ? Number(focusedHeatNumber)
    : null;
  const displayColumns = showNominationColumn ? HEAT_COLUMNS_WITH_NOMINATION : HEAT_NUMBERS;
  const activeTargetKey =
    focusedTargetType === "nomination"
      ? "nomination"
      : activeHeatNumber
        ? getHeatTargetKey(activeHeatNumber)
        : null;
  const gridTemplate = useMemo(() => {
    if (showNominationColumn && showResults) {
      return "grid-cols-[repeat(14,minmax(42px,1fr))_minmax(42px,1fr)_minmax(42px,1fr)_minmax(42px,1fr)_minmax(34px,1fr)]";
    }
    if (showNominationColumn) {
      return "grid-cols-[repeat(14,minmax(42px,1fr))_minmax(42px,1fr)_minmax(42px,1fr)]";
    }
    return showResults
      ? "grid-cols-[repeat(15,minmax(42px,1fr))_minmax(42px,1fr)_minmax(34px,1fr)]"
      : "grid-cols-[repeat(15,minmax(42px,1fr))]";
  }, [showNominationColumn, showResults]);
  const minGridWidth = showNominationColumn
    ? showResults
      ? "min-w-[778px]"
      : "min-w-[702px]"
    : showResults
      ? "min-w-[736px]"
      : "min-w-[660px]";
  const nominationRowTemplate = useMemo(() => {
    const homeRowCount = matrix.home.length;
    const awayRowCount = matrix.away.length;
    return [
      "2.25rem",
      "2rem",
      ...Array.from({ length: homeRowCount }, () => "2rem"),
      "2rem",
      ...Array.from({ length: awayRowCount }, () => "2rem"),
    ].join(" ");
  }, [matrix.away.length, matrix.home.length]);

  const updateNominationLayout = () => {
    const container = scrollRef.current;
    const anchor = nominationAnchorRef.current;
    if (!container || !anchor || !showNominationColumn) {
      setNominationLayout((current) => (current === null ? current : null));
      return;
    }

    const nextLayout = {
      left: anchor.offsetLeft,
      width: anchor.offsetWidth,
      height: container.scrollHeight,
    };
    setNominationLayout((current) =>
      current &&
      current.left === nextLayout.left &&
      current.width === nextLayout.width &&
      current.height === nextLayout.height
        ? current
        : nextLayout
    );
  };

  const updateScrollPositionState = (container) => {
    if (!container) return;
    const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);
    const atRightEdge = maxScrollLeft > 0 && maxScrollLeft - container.scrollLeft <= 2;

    setIsCompressed((current) => {
      // Separate enter/exit thresholds prevent state oscillation during
      // sub-pixel momentum scrolling in Android WebView.
      const shouldCompress = current ? container.scrollLeft > 8 : container.scrollLeft > 32;
      return current === shouldCompress ? current : shouldCompress;
    });
    setIsSnappedToRight((current) => (current === atRightEdge ? current : atRightEdge));
  };

  const isFocusedTargetVisible = () => {
    const container = scrollRef.current;
    if (!activeTargetKey) return false;
    const node = targetRefs.current.get(activeTargetKey);
    if (!container || !node) return false;

    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const visibleLeft = Math.max(containerRect.left, nodeRect.left);
    const visibleRight = Math.min(containerRect.right, nodeRect.right);
    const visibleWidth = Math.max(0, visibleRight - visibleLeft);
    const nodeWidth = Math.max(1, nodeRect.width);
    const visibleRatio = visibleWidth / nodeWidth;

    return visibleRatio >= 0.9;
  };

  const reportFocusedHeatVisibility = () => {
    onFocusedHeatVisibilityChange?.(isFocusedTargetVisible());
  };

  useEffect(() => {
    if (!isSidebarManualExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      setIsSidebarExpanded(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [isSidebarManualExpanded]);

  const handleScroll = (event) => {
    if (isNativeAndroid) {
      const container = event.currentTarget;
      setIsCompressed((current) => {
        const shouldCompress = current ? container.scrollLeft > 8 : container.scrollLeft > 32;
        return current === shouldCompress ? current : shouldCompress;
      });
      window.clearTimeout(scrollSettleTimeoutRef.current);
      scrollSettleTimeoutRef.current = window.setTimeout(() => {
        updateScrollPositionState(container);
        updateNominationLayout();
        reportFocusedHeatVisibility();
      }, 120);
      return;
    }

    if (window.performance.now() >= ignoreSidebarCollapseUntilRef.current) {
      setIsSidebarExpanded(false);
    }
    updateScrollPositionState(event.currentTarget);
    updateNominationLayout();
    reportFocusedHeatVisibility();
  };

  const handleScrollAreaPointerDown = () => {
    if (!isNativeAndroid || !isSidebarExpanded) return;
    setIsSidebarExpanded(false);
  };

  useEffect(
    () => () => {
      window.clearTimeout(scrollSettleTimeoutRef.current);
    },
    []
  );

  const handleHeatColumnClick = (heatNumber) => {
    onHeatClick?.(heatNumber);
  };

  const handleScrollAreaClickCapture = (event) => {
    if (!isSidebarManualExpanded) return;
    event.preventDefault();
    event.stopPropagation();
    setIsSidebarExpanded(false);
  };

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      updateScrollPositionState(scrollRef.current);
      updateNominationLayout();
      reportFocusedHeatVisibility();
    });
    const timeoutId = window.setTimeout(() => {
      updateScrollPositionState(scrollRef.current);
      updateNominationLayout();
      reportFocusedHeatVisibility();
    }, 320);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [activeTargetKey, isCompressed, isSidebarExpanded, onFocusedHeatVisibilityChange, showNominationColumn]);

  useEffect(() => {
    const requestedTargetKey =
      scrollTargetType === "nomination"
        ? "nomination"
        : Number.isFinite(Number(scrollToHeatNumber)) && Number(scrollToHeatNumber) > 0
          ? getHeatTargetKey(Number(scrollToHeatNumber))
          : null;
    if (!requestedTargetKey || !focusRequestKey) return undefined;

    const container = scrollRef.current;
    const targetNode = targetRefs.current.get(requestedTargetKey);
    if (!container || !targetNode) return undefined;

    const containerRect = container.getBoundingClientRect();
    const targetRect = targetNode.getBoundingClientRect();
    const nextLeft = Math.max(
      0,
      container.scrollLeft + (targetRect.left - containerRect.left)
    );
    container.scrollTo({ left: nextLeft, behavior: "smooth" });
    updateNominationLayout();
    reportFocusedHeatVisibility();
    setPulseTargetKey(requestedTargetKey);

    let rafId = 0;
    const startedAt = window.performance.now();
    let timeoutId = 0;

    const syncVisibilityAfterScroll = () => {
      updateNominationLayout();
      reportFocusedHeatVisibility();
      if (isFocusedTargetVisible()) return;
      if (window.performance.now() - startedAt > 700) return;
      rafId = window.requestAnimationFrame(syncVisibilityAfterScroll);
    };

    rafId = window.requestAnimationFrame(syncVisibilityAfterScroll);

    timeoutId = window.setTimeout(() => {
      reportFocusedHeatVisibility();
    }, 350);

    const pulseTimeoutId = window.setTimeout(() => {
      setPulseTargetKey((current) =>
        current === requestedTargetKey ? null : current
      );
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
      window.clearTimeout(pulseTimeoutId);
      window.cancelAnimationFrame(rafId);
    };
  }, [focusRequestKey, onFocusedHeatVisibilityChange, scrollTargetType, scrollToHeatNumber, showNominationColumn]);

  useEffect(() => {
    updateNominationLayout();
    const handleResize = () => {
      updateScrollPositionState(scrollRef.current);
      updateNominationLayout();
      reportFocusedHeatVisibility();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showNominationColumn]);

  if (!matrix.hasHeats) {
    return (
      <div className="rounded-xl border border-dashed border-border/80 bg-muted/25 p-5 text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-background/80 text-muted-foreground shadow-sm">
          <ListOrdered className="h-5 w-5" />
        </div>
        <div className="mt-3 space-y-1">
          <p className="text-sm font-medium text-foreground">{emptyText}</p>
          <p className="text-xs text-muted-foreground">
            Visas när körschema och laguppställning är färdigställda.
          </p>
        </div>
      </div>
    );
  }

  const isSidebarVisuallyCompact = isCompressed && !isSidebarExpanded;

  const renderSidebarContent = (isCompactView) => (
    <>
      <div className="relative flex h-9 items-center border-b bg-muted px-1.5 font-semibold text-foreground">
        <div className={cn("flex min-w-0 items-center gap-1.5", isCompactView && "hidden")}>
          <span className="truncate text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Förare
          </span>
          <CircleArrowDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-300 ease-in-out",
              isCompactView ? "-rotate-90" : "rotate-0"
            )}
          />
        </div>
        <div
          className={cn(
            "absolute inset-y-0 left-1.5 right-1.5 items-center gap-1",
            isCompactView ? "flex" : "hidden"
          )}
        >
          <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
            Heat
          </span>
          <CircleArrowDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-300 ease-in-out",
              isCompactView ? "-rotate-90" : "rotate-0"
            )}
          />
        </div>
      </div>

      {[
        ["home", homeTeamName, matrix.home],
        ["away", awayTeamName, matrix.away],
      ].map(([side, teamName, rows]) => (
        <div key={`heat-sidebar-${side}`}>
          <div
            className={cn(
              "relative h-8 overflow-hidden border-b bg-muted/60 px-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
            )}
          >
            <div
              className={cn(
                "absolute top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center transition-[left,transform] duration-300 ease-in-out will-change-[left,transform]",
                isCompactView ? "left-1/2 -translate-x-1/2 -translate-y-1/2" : "left-1.5"
              )}
            >
              <TeamLogo
                teamName={teamName}
                size={20}
                className="block h-4.5 w-4.5 shrink-0 object-contain"
              />
            </div>
            <div
              className={cn(
                "absolute inset-y-0 left-7 right-1.5 items-center overflow-hidden",
                isCompactView ? "hidden" : "flex"
              )}
            >
              <span className="truncate whitespace-nowrap">{teamName}</span>
            </div>
          </div>

          {rows.map((row, rowIndex) => {
            const isActiveHeatRider =
              activeHeatNumber != null && Boolean(row.cells?.[activeHeatNumber]);

            return (
              <div
                key={row.key}
                className={cn(
                  "relative h-8 overflow-hidden border-b font-medium transition-colors duration-150 ease-out",
                  rowIndex % 2 === 0 ? "bg-background" : "bg-muted/20",
                  isActiveHeatRider && "bg-primary/10 shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72)]"
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 min-w-0 items-center gap-1 px-1",
                    isCompactView ? "hidden" : "flex"
                  )}
                >
                  <span className="w-3.5 shrink-0 text-right text-[10px] text-muted-foreground">
                    {row.lineupNo ? `${row.lineupNo}.` : "-"}
                  </span>
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-foreground">{row.name}</span>
                    {row.isGuest ? <GuestRiderBadge iconOnly className="shrink-0 px-1" /> : null}
                  </span>
                  {row.isSubstitute ? (
                    <span className="rounded-full bg-emerald-500/10 px-1 py-0 text-[9px] font-semibold text-emerald-700">
                      Inbytt
                    </span>
                  ) : null}
                  {row.isReplaced ? (
                    <span className="rounded-full bg-amber-500/10 px-1 py-0 text-[9px] font-semibold text-amber-700">
                      Utbytt
                    </span>
                  ) : null}
                  {row.isReserve ? (
                    <span className="rounded-full bg-muted-foreground/10 px-1 py-0 text-[9px] font-semibold text-muted-foreground">
                      R
                    </span>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "absolute inset-0 items-center gap-1 px-1",
                    isCompactView ? "flex" : "hidden"
                  )}
                  title={row.name}
                >
                  <span className="w-3.5 shrink-0 text-right text-[10px] text-muted-foreground">
                    {row.lineupNo ? `${row.lineupNo}.` : "-"}
                  </span>
                  <span className="rounded-full bg-foreground px-1.5 py-0.5 text-[9px] font-bold text-background">
                    {getInitials(row.name)}
                  </span>
                  {row.isSubstitute ? (
                    <span className="rounded-full bg-emerald-500/10 px-1 py-0 text-[9px] font-semibold text-emerald-700">
                      I
                    </span>
                  ) : null}
                  {row.isReplaced ? (
                    <span className="rounded-full bg-amber-500/10 px-1 py-0 text-[9px] font-semibold text-amber-700">
                      U
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      {hasHeatTimes ? (
        <div className="relative h-8 overflow-hidden border-b bg-sky-500/5 font-medium">
          <div
            className={cn(
              "absolute inset-0 min-w-0 items-center gap-1.5 px-1.5",
              isCompactView ? "hidden" : "flex"
            )}
          >
            <Clock3 className="h-3.5 w-3.5 shrink-0 text-sky-700 dark:text-sky-300" />
            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-800 dark:text-sky-200">
              Heattid
            </span>
          </div>
          <div
            className={cn(
              "absolute inset-0 items-center justify-center gap-1 px-1",
              isCompactView ? "flex" : "hidden"
            )}
          >
            <Clock3 className="h-3.5 w-3.5 shrink-0 text-sky-700 dark:text-sky-300" />
          </div>
        </div>
      ) : null}
    </>
  );

  return (
    <div
      ref={rootRef}
      className={cn(
        "relative flex w-full max-w-full overflow-hidden rounded-xl border bg-background text-[11px]",
        isNativeAndroid && "native-v3-heat-matrix"
      )}
    >
      <div
        ref={sidebarRef}
        role="button"
        tabIndex={0}
        aria-label={isSidebarCompact ? "Visa fulla förarnamn" : "Minimera förarnamn"}
        onClick={() => {
          if (!isCompressed) return;
          ignoreSidebarCollapseUntilRef.current = window.performance.now() + 320;
          setIsSidebarExpanded((current) => !current);
        }}
        onKeyDown={(event) => {
          if (!isCompressed) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          ignoreSidebarCollapseUntilRef.current = window.performance.now() + 320;
          setIsSidebarExpanded((current) => !current);
        }}
        className={cn(
          "relative shrink-0 overflow-visible",
          !isNativeAndroid && "transition-[width] duration-300 ease-in-out will-change-[width]",
          isNativeAndroid && "z-[4]",
          isCompressed && "cursor-pointer select-none",
          isSidebarCompact ? "w-[70px]" : "w-[176px]"
        )}
      >
        <div
          className={cn(
            "native-v3-matrix-sidebar-surface overflow-hidden bg-background border-r-2 border-border",
            isNativeAndroid ? "relative h-full w-full" : "absolute inset-y-0 left-0 w-full",
            !isSidebarOverlayActive && "shadow-[6px_0_18px_rgba(15,23,42,0.06)]",
            !isNativeAndroid && isSidebarOverlayActive && "opacity-0"
          )}
        >
        {isNativeAndroid ? renderSidebarContent(isSidebarCompact) : !isSidebarOverlayActive ? (
        <>
        <div className="relative flex h-9 items-center border-b bg-muted px-1.5 font-semibold text-foreground">
          <div
            className={cn(
              "flex min-w-0 items-center gap-1.5",
              isSidebarVisuallyCompact
                ? "hidden"
                : "translate-x-0 opacity-100"
            )}
          >
            <span className="truncate text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              Förare
            </span>
            <CircleArrowDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-300 ease-in-out",
                isSidebarVisuallyCompact ? "-rotate-90" : "rotate-0"
              )}
            />
          </div>
          <div
            className={cn(
              "absolute inset-y-0 left-1.5 right-1.5 flex items-center gap-1",
              isSidebarVisuallyCompact
                ? "translate-x-0 opacity-100"
                : "hidden"
            )}
          >
            <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
              Heat
            </span>
            <CircleArrowDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-300 ease-in-out",
                isSidebarVisuallyCompact ? "-rotate-90" : "rotate-0"
              )}
            />
          </div>
        </div>

        {[
          ["home", homeTeamName, matrix.home],
          ["away", awayTeamName, matrix.away],
        ].map(([side, teamName, rows]) => (
          <div key={`heat-sidebar-${side}`}>
            <div
              className={cn(
                "relative h-8 overflow-hidden border-b bg-muted/60 px-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "absolute top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center transition-[left,transform] duration-300 ease-in-out will-change-[left,transform]",
                  isSidebarVisuallyCompact ? "left-1/2 -translate-x-1/2 -translate-y-1/2" : "left-1.5"
                )}
              >
                <TeamLogo
                  teamName={teamName}
                  size={20}
                  className="block h-4.5 w-4.5 shrink-0 object-contain"
                />
              </div>
              <div
                className={cn(
                  "absolute inset-y-0 left-7 right-1.5 flex items-center overflow-hidden transition-[opacity,transform] duration-0 ease-out will-change-[opacity,transform]",
                  isSidebarVisuallyCompact
                    ? "pointer-events-none translate-x-0 opacity-0 delay-0"
                    : "translate-x-0 opacity-100 delay-75"
                )}
              >
                <span className="truncate whitespace-nowrap">{teamName}</span>
              </div>
            </div>

            {rows.map((row, rowIndex) => {
              const isActiveHeatRider =
                activeHeatNumber != null && Boolean(row.cells?.[activeHeatNumber]);

              return (
              <div
                key={row.key}
                className={cn(
                  "relative h-8 overflow-hidden border-b font-medium transition-colors duration-150 ease-out",
                  rowIndex % 2 === 0 ? "bg-background" : "bg-muted/20",
                  isActiveHeatRider && "bg-primary/10 shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72)]"
                )}
              >
                <div
                  className={cn(
                    "absolute inset-0 flex min-w-0 items-center gap-1 px-1 transition-[opacity,transform] duration-0 ease-out",
                    isSidebarVisuallyCompact
                      ? "pointer-events-none -translate-x-2 opacity-0"
                      : "translate-x-0 opacity-100"
                  )}
                >
                  <span className="w-3.5 shrink-0 text-right text-[10px] text-muted-foreground">
                    {row.lineupNo ? `${row.lineupNo}.` : "-"}
                  </span>
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate text-foreground">{row.name}</span>
                    {row.isGuest ? <GuestRiderBadge iconOnly className="shrink-0 px-1" /> : null}
                  </span>
                  {row.isSubstitute ? (
                    <span className="rounded-full bg-emerald-500/10 px-1 py-0 text-[9px] font-semibold text-emerald-700">
                      Inbytt
                    </span>
                  ) : null}
                  {row.isReplaced ? (
                    <span className="rounded-full bg-amber-500/10 px-1 py-0 text-[9px] font-semibold text-amber-700">
                      Utbytt
                    </span>
                  ) : null}
                  {row.isReserve ? (
                    <span className="rounded-full bg-muted-foreground/10 px-1 py-0 text-[9px] font-semibold text-muted-foreground">
                      R
                    </span>
                  ) : null}
                </div>
                <div
                  className={cn(
                    "absolute inset-0 flex items-center gap-1 px-1 transition-[opacity,transform] duration-0 ease-out",
                    isSidebarVisuallyCompact
                      ? "translate-x-0 opacity-100 delay-110"
                      : "pointer-events-none translate-x-0 opacity-0 delay-0"
                  )}
                  title={row.name}
                >
                  <span className="w-3.5 shrink-0 text-right text-[10px] text-muted-foreground">
                    {row.lineupNo ? `${row.lineupNo}.` : "-"}
                  </span>
                  <span className="rounded-full bg-foreground px-1.5 py-0.5 text-[9px] font-bold text-background">
                    {getInitials(row.name)}
                  </span>
                  {row.isSubstitute ? (
                    <span className="rounded-full bg-emerald-500/10 px-1 py-0 text-[9px] font-semibold text-emerald-700">
                      I
                    </span>
                  ) : null}
                  {row.isReplaced ? (
                    <span className="rounded-full bg-amber-500/10 px-1 py-0 text-[9px] font-semibold text-amber-700">
                      U
                    </span>
                  ) : null}
                </div>
              </div>
            )})}
          </div>
        ))}
        {hasHeatTimes ? (
          <div className="relative h-8 overflow-hidden border-b bg-sky-500/5 font-medium">
            <div
              className={cn(
                "absolute inset-0 flex min-w-0 items-center gap-1.5 px-1.5 transition-[opacity,transform] duration-0 ease-out",
                isSidebarVisuallyCompact
                  ? "pointer-events-none -translate-x-2 opacity-0"
                  : "translate-x-0 opacity-100"
              )}
            >
              <Clock3 className="h-3.5 w-3.5 shrink-0 text-sky-700 dark:text-sky-300" />
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-800 dark:text-sky-200">
                Heattid
              </span>
            </div>
            <div
              className={cn(
                "absolute inset-0 flex items-center justify-center gap-1 px-1 transition-[opacity,transform] duration-0 ease-out",
                isSidebarVisuallyCompact
                  ? "translate-x-0 opacity-100 delay-110"
                  : "pointer-events-none translate-x-0 opacity-0 delay-0"
              )}
            >
              <Clock3 className="h-3.5 w-3.5 shrink-0 text-sky-700 dark:text-sky-300" />
            </div>
          </div>
        ) : null}
        </>
        ) : null}
        </div>
        {isNativeAndroid && isSidebarOverlayActive ? (
          <div className="native-v3-matrix-sidebar-overlay absolute inset-y-0 left-0 z-[7] w-[176px] overflow-hidden border-r-2 border-border bg-background">
            {renderSidebarContent(false)}
          </div>
        ) : null}
        {!isNativeAndroid && isCompressed && isSnappedToRight ? (
          <div
            className={cn(
              "absolute inset-y-0 left-0 z-[5] overflow-hidden border-r-2 border-border bg-background transition-[width] duration-300 ease-in-out",
              isSidebarOverlayActive
                ? "w-[176px]"
                : "pointer-events-none w-[70px]"
            )}
          >
            {renderSidebarContent(!isSidebarOverlayActive)}
          </div>
        ) : null}
      </div>

      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onPointerDown={handleScrollAreaPointerDown}
        onClickCapture={handleScrollAreaClickCapture}
        className="min-w-0 flex-1 overflow-x-auto"
      >
        <div className={cn("relative", minGridWidth)}>
          <div
            className={cn(
              "grid h-9 border-b border-border/80 bg-muted/50",
              gridTemplate
            )}
          >
            {displayColumns.map((column, columnIndex) => {
              if (column === "nomination") {
                return (
                  <div
                    key="heat-head-nomination"
                    ref={(node) => {
                      nominationAnchorRef.current = node;
                      if (node) {
                        targetRefs.current.set("nomination", node);
                      } else {
                        targetRefs.current.delete("nomination");
                      }
                    }}
                    className={cn(
                      "border-l first:border-l-0",
                      activeTargetKey === "nomination" && "bg-primary/10",
                      activeTargetKey === "nomination" &&
                        "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                      pulseTargetKey === "nomination" &&
                        "bg-primary/15 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]"
                    )}
                  />
                );
              }

              const heatNumber = Number(column);
              const targetKey = getHeatTargetKey(heatNumber);
              return (
                <button
                  type="button"
                  key={`heat-head-${heatNumber}`}
                  ref={(node) => {
                    if (node) {
                      targetRefs.current.set(targetKey, node);
                    } else {
                      targetRefs.current.delete(targetKey);
                    }
                  }}
                  className={cn(
                    "flex items-center justify-center border-l px-1.5 font-semibold text-foreground first:border-l-0 disabled:cursor-default",
                    activeTargetKey === targetKey && "bg-primary/10",
                    activeTargetKey === targetKey &&
                      "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                    pulseTargetKey === targetKey &&
                      "bg-primary/15 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]",
                    columnIndex === 0 && "border-l-0"
                  )}
                  onClick={() => handleHeatColumnClick(heatNumber)}
                  disabled={!onHeatClick}
                >
                  {heatNumber}
                </button>
              );
            })}
            {showResults ? (
              <>
                <div className="flex items-center justify-center border-l px-1 font-semibold text-foreground">Tot</div>
                <div className="flex items-center justify-center border-l px-1 font-semibold text-foreground">H</div>
              </>
            ) : null}
          </div>

          {[
            ["home", matrix.home],
            ["away", matrix.away],
          ].map(([side, rows]) => (
            <div key={`heat-grid-${side}`}>
              <div
                className={cn(
                  "grid h-8 border-b border-border/80 bg-muted/30 shadow-[inset_0_1px_0_rgba(15,23,42,0.18),inset_0_-1px_0_rgba(15,23,42,0.18)]",
                  gridTemplate
                )}
              >
                {displayColumns.map((column, index) => {
                  if (column === "nomination") {
                    return (
                      <div
                        key={`${side}-team-score-nomination`}
                        className={cn(
                          "border-l",
                          activeTargetKey === "nomination" && "bg-primary/5",
                          activeTargetKey === "nomination" &&
                            "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                          pulseTargetKey === "nomination" &&
                            "bg-primary/10 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]"
                        )}
                      />
                    );
                  }

                  const heatNumber = Number(column);
                  const targetKey = getHeatTargetKey(heatNumber);
                  const teamHeatPoints = matrix.teams?.[side]?.heatPoints?.[heatNumber];
                  return (
                    <button
                      type="button"
                      key={`${side}-team-score-${heatNumber}`}
                      className={cn(
                        "flex items-center justify-center border-l px-1 text-[10px] font-semibold text-muted-foreground first:border-l-0 disabled:cursor-default",
                        index === 0 && "border-l-0",
                        activeTargetKey === targetKey && "bg-primary/5",
                        activeTargetKey === targetKey &&
                          "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                        pulseTargetKey === targetKey &&
                          "bg-primary/10 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]"
                      )}
                      onClick={() => handleHeatColumnClick(heatNumber)}
                      disabled={!onHeatClick}
                    >
                      {showResults && teamHeatPoints != null ? teamHeatPoints : null}
                    </button>
                  );
                })}
                {showResults ? (
                  <>
                    <div className="flex items-center justify-center border-l bg-primary/10 px-1 text-sm font-extrabold text-foreground">
                      {matrix.teams?.[side]?.total ?? 0}
                    </div>
                    <div className="border-l" />
                  </>
                ) : null}
              </div>

              {rows.map((row, rowIndex) => (
                <div
                  key={`${row.key}-grid`}
                  className={cn(
                    "grid h-8 border-b border-border/80",
                    gridTemplate,
                    rowIndex % 2 === 0 ? "bg-background" : "bg-muted/20"
                  )}
                >
                  {displayColumns.map((column, index) => {
                    if (column === "nomination") {
                      return (
                        <div
                          key={`${row.key}-nomination`}
                          className={cn(
                            "border-l",
                            index === 0 && "border-l-0",
                            activeTargetKey === "nomination" && "bg-primary/5",
                            activeTargetKey === "nomination" &&
                              "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                            pulseTargetKey === "nomination" &&
                              "bg-primary/10 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]"
                          )}
                        />
                      );
                    }

                    const heatNumber = Number(column);
                    const targetKey = getHeatTargetKey(heatNumber);
                    const cell = row.cells[heatNumber];
                    const content = getResultDisplay(cell, showResults);
                    const isNonCompleted = showResults && cell?.result?.status && cell?.result?.status !== "completed";
                    const isCompleted = showResults && cell?.result?.status === "completed";

                    return (
                      <button
                        type="button"
                        key={`${row.key}-${heatNumber}`}
                        className={cn(
                          "relative flex items-center justify-center border-l px-0.5 first:border-l-0 disabled:cursor-default",
                          activeTargetKey === targetKey && "bg-primary/5",
                          activeTargetKey === targetKey &&
                            "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                          pulseTargetKey === targetKey &&
                            "bg-primary/10 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]",
                          index === 0 && "border-l-0"
                        )}
                        onClick={() => handleHeatColumnClick(cell?.heat || heatNumber)}
                        disabled={!onHeatClick}
                        title={cell ? `Heat ${heatNumber}, gate ${cell.gate}` : `Heat ${heatNumber}`}
                      >
                        {cell ? (
                          <>
                            <span
                              style={isNativeAndroid ? getHelmetAccentStyle(cell.helmetColor) : undefined}
                              className={cn(
                                "native-v3-matrix-result-cell inline-flex min-w-[28px] items-center justify-center gap-0.5 rounded-full border px-0.5 py-0.5 text-[10px] font-bold",
                                isCompleted
                                  ? getMutedHelmetClass(cell.helmet?.label)
                                  : isNonCompleted
                                    ? "border-red-300 bg-red-50 text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200"
                                    : cell.helmet.className
                              )}
                            >
                              {showResults && (isCompleted || isNonCompleted) ? (
                                content
                              ) : (
                                <>
                                  <span>{cell.helmet?.label}</span>
                                </>
                              )}
                            </span>
                            <span className="pointer-events-none absolute bottom-[2px] right-[3px] text-[8px] font-semibold leading-none text-foreground/75">
                              {cell.gate}
                            </span>
                          </>
                        ) : null}
                      </button>
                    );
                  })}
                  {showResults ? (
                    <>
                      <div className="flex items-center justify-center border-l px-1 font-semibold text-foreground">
                        {formatPoints(row.totalBase, row.totalBonus)}
                      </div>
                      <div className="flex items-center justify-center border-l px-1 text-muted-foreground">
                        {row.starts || "-"}
                      </div>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          ))}

          {hasHeatTimes ? (
            <div
              className={cn(
                "grid h-8 border-b border-border/80 bg-sky-500/5",
                gridTemplate
              )}
            >
              {displayColumns.map((column, index) => {
                if (column === "nomination") {
                  return <div key="heat-time-nomination" className={cn("border-l", index === 0 && "border-l-0")} />;
                }

                const heatNumber = Number(column);
                const targetKey = getHeatTargetKey(heatNumber);
                const timeValue = matrix.heatTimes?.[heatNumber] || "";

                return (
                  <button
                    type="button"
                    key={`heat-time-${heatNumber}`}
                    className={cn(
                      "flex items-center justify-center border-l px-1 text-[10px] font-semibold text-sky-900 first:border-l-0 disabled:cursor-default dark:text-sky-100",
                      activeTargetKey === targetKey && "bg-primary/5",
                      activeTargetKey === targetKey &&
                        "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                      pulseTargetKey === targetKey &&
                        "bg-primary/10 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]",
                      index === 0 && "border-l-0"
                    )}
                    onClick={() => handleHeatColumnClick(heatNumber)}
                    disabled={!onHeatClick}
                    title={timeValue ? `Heattid ${timeValue}` : `Heat ${heatNumber}`}
                  >
                    {timeValue || null}
                  </button>
                );
              })}
              {showResults ? (
                <>
                  <div className="border-l" />
                  <div className="border-l" />
                </>
              ) : null}
            </div>
          ) : null}

          {showNominationColumn && nominationLayout ? (
            <button
              type="button"
              onClick={onNominationClick}
              disabled={!onNominationClick}
              className={cn(
                "absolute top-0 z-[1] flex items-center justify-center border-l border-border bg-background transition-colors duration-150 disabled:cursor-default",
                activeTargetKey === "nomination" && "bg-primary/12",
                activeTargetKey === "nomination" &&
                  "shadow-[inset_2px_0_0_0_rgba(220,38,38,0.72),inset_-2px_0_0_0_rgba(220,38,38,0.72)]",
                pulseTargetKey === "nomination" &&
                  "bg-primary/18 shadow-[inset_3px_0_0_0_rgba(220,38,38,0.95),inset_-3px_0_0_0_rgba(220,38,38,0.95)]",
                onNominationClick && "hover:bg-primary/10"
              )}
              style={{
                left: nominationLayout.left,
                width: nominationLayout.width,
                height: nominationLayout.height,
              }}
              title={
                nominationCompleted
                  ? "Redigera nominering"
                  : nominationAvailable
                    ? "Välj nominering"
                    : "Nominering blir tillgänglig inför heat 15"
              }
            >
              <div
                className="grid h-full w-full px-1"
                style={{ gridTemplateRows: nominationRowTemplate }}
              >
                <span
                  className={cn(
                    "col-start-1 row-start-4 row-end-5 mx-auto flex h-5 w-5 items-center justify-center self-center rounded-full border",
                    nominationCompleted
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200"
                      : nominationAvailable
                        ? "border-primary/30 bg-primary/10 text-primary"
                        : "border-border bg-muted/80 text-muted-foreground"
                  )}
                >
                  <Crosshair className="h-3 w-3" />
                </span>
                {"Nominering".split("").map((letter, index) => (
                  <span
                    key={`nomination-letter-${index}`}
                    className="col-start-1 flex items-center justify-center text-[16px] font-bold uppercase leading-none text-muted-foreground"
                    style={{ gridRowStart: index + 5, gridRowEnd: index + 6 }}
                  >
                    {letter}
                  </span>
                ))}
              </div>
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
